<template lang="pug">
  .panel-page-white
    .panel.mx-auto.body-1(v-if="!needCode")
      FormErrorAlert(v-show="message" :message="message" @dismiss="message = ''")
      h1 Reset Password
      p
        | You have received an email with a verification code. Please enter the verification code
        | below with a new password.
      form(autocomplete="off")
        .form-section
          .form-row
            .col-6
              .form-group
                label(for="verification-code" required) Verification Code
                input#verification-code.form-control(
                  v-model="verificationCode"
                  :class="{'is-invalid': verificationCodeInvalid}"
                )
                FormError(v-show="verificationCodeInvalid") Verification code is required.
          .form-row
            .col
              button.btn.btn-primary.mr-3(@click.prevent="submitVerificationCode") Submit
              router-link.btn.btn-light(:to="{name: 'my_account'}") Cancel
          .form-row(v-if="verified")
            .col
              ChangePasswordWidget(
                :message.sync="message"
                :current-password-needed="false"
                :username="username"
                :firstName="firstName"
                :lastName="lastName"
                @password="reset"
                @tryPassword="verificationCodeTouched = true"
              )
    .panel.mx-auto.body-1(v-if="needCode")
      FormErrorAlert(v-show="message" :message="message" @dismiss="message = ''")
      h1 Forgot Password
      p
        | Enter the following information to have an email sent to you that will provide further instructions on
        | how to change your password. The email will be sent to the primary email address associated with your account.
      form(
        @submit.prevent="submit"
        autocomplete="off"
      )
        .form-section
          .form-row
            .col-6
              .form-group
                label(for="username" required) Username
                input#username.form-control(
                  v-model="username"
                  :class="{'is-invalid': usernameInvalid}"
                )
                FormError(v-show="usernameInvalid") Username is required.
          .form-row
            .col
              button.btn.btn-primary.mr-3(type="submit") Request Password Reset
              router-link(:to="{name: 'login'}") Back to Login Page
</template>

<script>
import * as validation from '@/lib/validation'
import ChangePasswordWidget from '@/components/ChangePasswordWidget'
import stdmsg from '@/lib/stdmsg'
import axios from '@/lib/axios'
import { globalSuccess, globalError } from '@/lib/utils'

export default {
  name: 'ForgotPassword',
  data() {
    return {
      message: '',
      verificationCode: '',
      needCode: true,
      username: '',
      firstName: '',
      lastName: '',
      verified: false,
      ...validation.mapTouched(['verificationCode', 'username']),
    }
  },
  computed: {
    ...validation.mapRequired(['verificationCode', 'username']),
    isVerified: function() {
      return this.verified
    },
  },
  methods: {
    ...validation.validationHelpers(),
    submitVerificationCode() {
      if (this.verificationCodeInvalid) {
        this.message = stdmsg.formError
        return
      }
      axios({ auth: false })
        .get('/api/verify_user_code', {
          params: {
            code: this.verificationCode,
            username: this.username,
          }
        })
        .then(resp => {
          globalSuccess(null, 'The verification was successful.')
          this.verified = true
        })
        .catch(err => globalError(err))
      axios({ auth: false })
        .get('/api/get_user_data', {
            params: {
              username: this.username,
            }
          })
          .then(resp => {
            this.firstName = resp.data.firstName
            this.lastName = resp.data.lastName
          })
          .catch(err => globalError(err))
    },
    async submit() {
      this.markAllTouched()
      if (this.usernameInvalid) {
        this.message = stdmsg.formError
        return
      }
      try {
        axios({ auth: false }).post('/api/forgot_password', {
          username: this.username,
        })
        this.needCode = false
        this.verificationCodeTouched = false
      } catch (err) {
        // We don't actually want to alert an error because it could be used for guessing usernames.
      }
      globalSuccess(
        null,
        'If your user was found, you will receive an email. Please contact your administrator if you do not.',
      )
    },
    reset({ newPassword }) {
      if (this.verificationCodeInvalid) {
        this.message = stdmsg.formError
        return
      }
      axios({ auth: false })
        .post('/api/reset_password', {
          code: this.verificationCode,
          password: newPassword,
          username: this.username,
        })
        .then(() => {
          globalSuccess(null, 'The password was reset successfully.')
          this.$router.push({ name: 'login' })
        })
        .catch(err => globalError(err))
    },
  },
  beforeRouteEnter(to, _from, next) {
    if (to.params.forceCode)
      next(vm => {
        vm.needCode = false
        vm.username = to.params.username
      })
    else next()
  },
  components: { ChangePasswordWidget },
}
</script>

<style lang="scss" scoped>
.panel {
  padding: 3rem;
}
.form-section {
  background-color: #fcfcfc;
  padding: 1rem;
}
</style>
